/**
 * Ribbon Service - Manages Office ribbon state updates
 *
 * Handles updating the ribbon status indicators based on the current worksheet's status.
 */

import { getStatusItemsBySpreadsheet } from "./status-items.service";
import { getOpenSpreadsheetId, getTabColouringEnabled } from "./spreadsheet-metadata.service";
import { ExcelService } from "./excel.service";
import { normalizeStatusItemStatus, getStatusItemColor, getLowestStatus } from "../utils/statusItemHelpers";
import { environment } from "../config/environment";
import { isStandaloneMode } from "../utils/standalone-mode";

// Get the environment-specific prefix for ribbon control IDs
// local -> BDO.WorkflowIntelligenceLocal, dev -> BDO.WorkflowIntelligenceDev, prod -> BDO.WorkflowIntelligenceProd
function getRibbonIdPrefix(): string {
  const suffixMap = { local: 'Local', dev: 'Dev', prod: 'Prod' };
  return `BDO.WorkflowIntelligence${suffixMap[environment.name]}`;
}

const RIBBON_PREFIX = getRibbonIdPrefix();

// Ribbon control IDs - must match the Control ids in manifest.xml
// Split into two groups to ensure horizontal layout
const SHEET_STATUS_CONTROLS = {
  Open: `${RIBBON_PREFIX}.StatusOpen`,
  Processed: `${RIBBON_PREFIX}.StatusProcessed`,
  "Rework Required": `${RIBBON_PREFIX}.StatusRework`,
} as const;

const REVIEW_STATUS_CONTROLS = {
  "Quality Reviewed": `${RIBBON_PREFIX}.StatusQualityReviewed`,
  "Partner Reviewed": `${RIBBON_PREFIX}.StatusPartnerReviewed`,
} as const;

// Combined for type checking
const STATUS_CONTROL_IDS = {
  ...SHEET_STATUS_CONTROLS,
  ...REVIEW_STATUS_CONTROLS,
} as const;

type StatusType = keyof typeof STATUS_CONTROL_IDS;

/**
 * RibbonService - Singleton service that manages ribbon state
 */
export class RibbonService {
  private static instance: RibbonService;
  private unsubscribeWorksheetActivated: (() => Promise<void>) | null = null;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): RibbonService {
    if (!RibbonService.instance) {
      RibbonService.instance = new RibbonService();
    }
    return RibbonService.instance;
  }

  /**
   * Initialize the ribbon service - sets up worksheet change listener.
   * No-op in standalone / dialog mode where there's no Excel ribbon to update
   * and no worksheet events to subscribe to.
   */
  public async initialize(): Promise<void> {
    if (isStandaloneMode()) return;
    if (this.isInitialized) {
      console.log("RibbonService already initialized");
      return;
    }

    try {
      // Register for worksheet activation events
      this.unsubscribeWorksheetActivated = await ExcelService.onWorksheetActivated(
        this.handleWorksheetActivated.bind(this)
      );

      // Update ribbon for the current active worksheet
      await this.updateRibbonStatusIndicator();

      this.isInitialized = true;
      console.log("RibbonService initialized successfully");
    } catch (error) {
      console.error("Failed to initialize RibbonService:", error);
    }
  }

  /**
   * Handle worksheet activation event
   */
  private async handleWorksheetActivated(): Promise<void> {
    await this.updateRibbonStatusIndicator();
  }

  /**
   * Update the ribbon status indicator for the currently active worksheet.
   * This is the ONLY function that should be used to update the ribbon status buttons.
   * If no status items exist for the worksheet, all buttons are disabled and tab color is not changed.
   */
  public async updateRibbonStatusIndicator(): Promise<void> {
    if (isStandaloneMode()) return;
    try {
      const worksheetName = await ExcelService.getActiveWorksheetName();
      const currentStatus = await this.getStatusForActiveWorksheet();
      const hasStatusItems = currentStatus !== null;
      console.log(`Updating ribbon for worksheet "${worksheetName}" with status: ${currentStatus ?? "none (no status items)"}`);

      // Local alias for the deeply-nested ribbon control type. Office.js types
      // every layer (tabs, groups, controls) as optional, so we have to unwrap
      // each with NonNullable<> to land on the underlying Control[] shape.
      type RibbonControls = NonNullable<
        NonNullable<Office.RibbonUpdaterData["tabs"]>[number]["groups"]
      >[number]["controls"];

      // Build controls for Sheet Status group (Open, Processed, Rework)
      // If no status items exist for this worksheet, all buttons are disabled
      const sheetStatusControls: RibbonControls = [];
      for (const [status, controlId] of Object.entries(SHEET_STATUS_CONTROLS)) {
        sheetStatusControls.push({
          id: controlId,
          enabled: hasStatusItems && status === currentStatus,
        });
      }

      // Build controls for Review Status group (Quality Reviewed, Partner Reviewed)
      const reviewStatusControls: RibbonControls = [];
      for (const [status, controlId] of Object.entries(REVIEW_STATUS_CONTROLS)) {
        reviewStatusControls.push({
          id: controlId,
          enabled: hasStatusItems && status === currentStatus,
        });
      }

      const ribbonUpdate: Office.RibbonUpdaterData = {
        tabs: [
          {
            id: RIBBON_PREFIX,
            groups: [
              {
                id: `${RIBBON_PREFIX}.SheetStatus`,
                controls: sheetStatusControls,
              },
              {
                id: `${RIBBON_PREFIX}.ReviewStatus`,
                controls: reviewStatusControls,
              },
            ],
          },
        ],
      };

      await Office.ribbon.requestUpdate(ribbonUpdate);
      console.log(`Ribbon updated - enabled: ${currentStatus ?? "none"}`);

      // Update the worksheet tab colour to match the status (if enabled)
      // Only update tab color if there are status items for this worksheet
      if (hasStatusItems) {
        const tabColouringEnabled = await getTabColouringEnabled();
        if (tabColouringEnabled) {
          const tabColor = getStatusItemColor(currentStatus);
          await ExcelService.setWorksheetTabColor(worksheetName, tabColor);
          console.log(`Worksheet tab color updated to: ${tabColor}`);
        }
      }
    } catch (error) {
      console.error("Failed to update ribbon status indicator:", error);
    }
  }

  /**
   * Get the status for the active worksheet by querying the backend.
   * Returns the lowest status among all status items for the worksheet,
   * or null if no status items exist.
   */
  private async getStatusForActiveWorksheet(): Promise<StatusType | null> {
    try {
      const spreadsheetId = await getOpenSpreadsheetId();
      if (!spreadsheetId) {
        return null;
      }

      const worksheetInfo = await ExcelService.getActiveWorksheetInfo();
      const statusItems = await getStatusItemsBySpreadsheet(spreadsheetId);

      // Find ALL status items for this worksheet by ID
      const worksheetItems = statusItems.filter(
        (item) => item.sheetId === worksheetInfo.id && !item.isHidden
      );

      if (worksheetItems.length > 0) {
        // Get the lowest status among all items for this worksheet
        const statuses = worksheetItems.map((item) => item.status);
        const lowestStatus = getLowestStatus(statuses);
        // Ensure the status is a valid StatusType
        if (lowestStatus in STATUS_CONTROL_IDS) {
          return lowestStatus as StatusType;
        }
      }
    } catch (error) {
      console.error("Failed to get status for active worksheet:", error);
    }

    return null;
  }

  /**
   * Update just the tab colour for a specific worksheet (without ribbon update)
   * Use this when changing the status of a non-active worksheet
   */
  public async updateTabColorForWorksheet(worksheetName: string, status: string): Promise<void> {
    if (isStandaloneMode()) return;
    try {
      const tabColouringEnabled = await getTabColouringEnabled();
      if (tabColouringEnabled) {
        const normalized = normalizeStatusItemStatus(status);
        const tabColor = getStatusItemColor(normalized);
        await ExcelService.setWorksheetTabColor(worksheetName, tabColor);
        console.log(`Tab color for "${worksheetName}" updated to: ${tabColor}`);
      }
    } catch (error) {
      console.error(`Failed to update tab color for worksheet "${worksheetName}":`, error);
    }
  }

  /**
   * Sync tab colors for all worksheets based on their status items
   * If tab coloring is disabled, clears all worksheet tab colors
   * If a worksheet has no status items, its tab color is cleared
   */
  public async syncAllWorksheetTabColors(): Promise<void> {
    if (isStandaloneMode()) return;
    try {
      const tabColouringEnabled = await getTabColouringEnabled();
      const worksheets = await ExcelService.getAllWorksheetsInfo();

      if (!tabColouringEnabled) {
        // Clear all tab colors when disabled
        for (const worksheet of worksheets) {
          await ExcelService.setWorksheetTabColor(worksheet.name, "");
        }
        console.log("Tab coloring disabled - cleared all worksheet tab colors");
        return;
      }

      // Get all status items for the spreadsheet
      const spreadsheetId = await getOpenSpreadsheetId();
      if (!spreadsheetId) {
        console.log("No spreadsheet ID available, skipping tab color sync");
        return;
      }

      const statusItems = await getStatusItemsBySpreadsheet(spreadsheetId);

      // Update each worksheet's tab color based on its status items
      for (const worksheet of worksheets) {
        const worksheetItems = statusItems.filter(
          (item) => item.sheetId === worksheet.id && !item.isHidden
        );

        if (worksheetItems.length > 0) {
          // Get the lowest status and set the color
          const statuses = worksheetItems.map((item) => item.status);
          const lowestStatus = getLowestStatus(statuses);
          const tabColor = getStatusItemColor(lowestStatus);
          await ExcelService.setWorksheetTabColor(worksheet.name, tabColor);
        } else {
          // No status items for this worksheet - clear the tab color
          await ExcelService.setWorksheetTabColor(worksheet.name, "");
        }
      }

      console.log(`Synced tab colors for ${worksheets.length} worksheets`);
    } catch (error) {
      console.error("Failed to sync all worksheet tab colors:", error);
    }
  }

  /**
   * Clean up event handlers
   */
  public async dispose(): Promise<void> {
    if (this.unsubscribeWorksheetActivated) {
      await this.unsubscribeWorksheetActivated();
      this.unsubscribeWorksheetActivated = null;
    }
    this.isInitialized = false;
    console.log("RibbonService disposed");
  }
}

// Export singleton instance
export const ribbonService = RibbonService.getInstance();
